import { 
  Card, Typography, CardActions, CardContent, Box, 
  IconButton, Chip, Avatar, Divider, Tooltip
} from "@mui/material";
import Stack from "@mui/material/Stack";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/reducers/index";

// Icons
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import FileCopyTwoToneIcon from "@mui/icons-material/FileCopyTwoTone";
import PersonIcon from "@mui/icons-material/Person";

import { MatriceWrapperClientsData } from "../../interface/matrice";

interface MatrixBoxProps {
  matrice: MatriceWrapperClientsData;
  handleClientClick: (name: string) => void;
  setOpenModal: (matrice: MatriceWrapperClientsData) => void;
  handleDeleteConfirmation: (id: string) => void;
  handleDuplicateConfirmation: (id: string, name: string) => void;
}

const MatrixBox = ({
  matrice,
  handleClientClick,
  setOpenModal,
  handleDeleteConfirmation,
  handleDuplicateConfirmation,
}: MatrixBoxProps) => {
  const author = useSelector((state: RootState) => state.user.username);
  const isCreator = author === matrice.creator;

  return (
    <Card
      sx={{
        width: 320,
        height: 380,
        display: "flex",
        flexDirection: "column",
        borderRadius: "16px",
        transition: "all 0.3s ease-in-out",
        border: "1px solid #e2e8f0",
        boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)",
          borderColor: "#3b82f6",
        },
      }}
    >
      {/* Header avec accent de couleur */}
      <Box sx={{ p: 2, pb: 1, borderBottom: "1px solid #f1f5f9", bgcolor: "#fffaf0" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ maxWidth: "80%" }}>
            <Tooltip title={matrice.name} arrow>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  color: "#1e293b",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {matrice.name}
              </Typography>
            </Tooltip>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
              <PersonIcon sx={{ fontSize: 14, color: "#64748b" }} />
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500 }}>
                {matrice.creator}
              </Typography>
            </Stack>
          </Box>
          <Chip 
            label="Template" 
            size="small" 
              sx={{ bgcolor: "#3b82f6", color: "white", fontWeight: "bold", fontSize: "0.65rem" }} 
          />
        </Stack>
      </Box>

      <CardContent sx={{ flexGrow: 1, p: 2, display: "flex", flexDirection: "column" }}>
        {/* Description */}
        <Box sx={{ height: 80, overflow: "hidden", mb: 2 }}>
          {matrice?.desc ? (
            <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.6 }}>
              {matrice.desc}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ fontStyle: "italic", color: "#94a3b8", textAlign: "center", mt: 2 }}>
              Aucune description fournie
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 2, borderStyle: "dashed" }} />

        {/* Clients associés */}
        <Typography variant="overline" sx={{ fontWeight: 800, color: "#94a3b8", mb: 1, display: "block" }}>
          Clients associés
        </Typography>
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignContent: "flex-start",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": { bgcolor: "#e2e8f0", borderRadius: "10px" },
          }}
        >
          {matrice?.clients && matrice.clients.length > 0 ? (
            matrice.clients.map((client: any) => (
              <Chip
                key={client._id}
                label={client.name}
                size="small"
                onClick={() => handleClientClick(client.name)}
                sx={{
                  bgcolor: "#f1f5f9",
                  fontWeight: 500,
                  "&:hover": { bgcolor: "#e2e8f0" },
                  "& .MuiChip-label": { px: 1 }
                }}
              />
            ))
          ) : (
            <Typography variant="caption" sx={{ color: "#cbd5e1" }}>
              Aucun lien client
            </Typography>
          )}
        </Box>
      </CardContent>

      <Divider />

      {/* Actions */}
      <CardActions sx={{ justifyContent: "space-between", px: 2, py: 1, bgcolor: "#f8fafc" }}>
        <Box>
          <Tooltip title={isCreator ? "Modifier" : "Lecture seule"}>
            <span>
              <IconButton
                size="small"
                onClick={() => setOpenModal(matrice)}
                disabled={!isCreator}
                sx={{ color: isCreator ? "#64748b" : "#cbd5e1" }}
              >
                <EditTwoToneIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          
          <Tooltip title="Dupliquer">
            <IconButton
              size="small"
              onClick={() => handleDuplicateConfirmation(matrice._id, matrice.name)}
              sx={{ color: "#64748b" }}
            >
              <FileCopyTwoToneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {isCreator && (
          <Tooltip title="Supprimer">
            <IconButton
              size="small"
              onClick={() => handleDeleteConfirmation(matrice._id)}
              sx={{ 
                color: "#ef4444", 
                "&:hover": { bgcolor: "#fee2e2" } 
              }}
            >
              <DeleteTwoToneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </CardActions>
    </Card>
  );
};

export default MatrixBox;